import { TypeShopGroupHomepage } from '@contentful/types/TypeShopGroupHomepage';
import { TypeShopHomepage } from '@contentful/types/TypeShopHomepage';
import { makeAutoObservable } from 'mobx';
import { RootStore } from './root-store';
import { extractSubdomain } from '@util/getURL';

export interface Shop {
  type: 'shop';
  shopId: string;
  name: string;
  legalName: string;
  locationName: string;
  licenses: any[];
  socialProfiles: { url: string; type: string }[];
  shopImage?: string;
  chimeInfo?: ShopChimeInfo;
  shopGroup?: ShopGroupInfo;
  mls: ShopMls[];
  contact: ShopContact;
  associatedAdvisors?: any[];
  associatedTeams?: any[];
}

interface ShopGroupInfo {
  id: string;
  name: string;
  image?: string;
  chimeInfo?: ShopChimeInfo;
}

interface ShopMls {
  mlsId: string;
  mls: {
    id: string;
    name: string;
    osn: string;
  };
}

interface ShopContact {
  address: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  alternativeEmail?: string;
  website: string;
}

interface ShopChimeInfo {
  type: 'shop' | 'shopGroup';
  chimeOfficeId: number;
}

export interface ShopGroup {
  shopGroupId: string;
  shopGroupImage?: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  legalName: string;
  locationName: string;
  url: string;
  description: string | null;
  status: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  chimeInfo?: ShopChimeInfo;
  shops: ShopInfo[];
}

interface ShopInfo {
  shopId: string;
  shopImage?: string;
  name: string;
  locationName: string;
  legalName: string;
  licenses: any[];
  socialProfiles: { url: string; type: string }[];
  mls: ShopMls[];
  teams?: ShopTeamInfo[];
}

interface ShopTeamInfo {
  teamId: string;
  name: string;
  domain?: string;
  chimeInfo?: TeamChimeInfo;
}

interface TeamChimeInfo {
  type: 'team';
  chimeTeamId: number;
}

/**
 * Hold the shop data when on a shop subdomain (e.g. shop.host.com) or on a shop path (e.g. host.com/shops/shopname)
 */
export class ShopStore {
  public shop: Shop | null = null;
  public shopGroup: ShopGroup | null = null;
  public shopSlug: string | null = null;
  public shopPageData: TypeShopHomepage | TypeShopGroupHomepage | null = null;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  public setShop = (shop: Shop | null): void => {
    this.shop = shop;
  };

  public setShopGroup = (shopGroup: ShopGroup | null): void => {
    this.shopGroup = shopGroup;
  };

  public setShopSlug = (shopSlug: string | null): void => {
    this.shopSlug = shopSlug;
  };

  public setShopPageData = (
    shopPageData: TypeShopHomepage | TypeShopGroupHomepage | null,
  ): void => {
    this.shopPageData = shopPageData;
  };

  /** Returns the path prefix for shop URLs in the format '/shops/shopname', but only if we are not on a shop subdomain */
  public get shopPathPrefix(): string {
    const subdomain = typeof window !== 'undefined' ? extractSubdomain(window.location.host) : null;
    if (!subdomain && this.shopSlug) {
      return `/shops/${this.shopSlug}`;
    }

    return '';
  }

  /** If shop group map through shops shopIds, otherwise use shop's shopId if shop exists */
  public get shopIds(): string[] | null {
    if (this.shopGroup) {
      return this.shopGroup.shops.map((shop) => shop.shopId);
    }
    if (this.shop) {
      return this.shop.shopId?.split(',');
    }
    return null;
  }

  public get shopChimeId(): number | null {
    if (this.shop) {
      return this.shop.chimeInfo?.chimeOfficeId || null;
    }
    if (this.shopGroup) {
      return this.shopGroup.chimeInfo?.chimeOfficeId || null;
    }
    return null;
  }
}
